import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Empty, Input, Select, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { ClipboardList, Hourglass, Phone, RotateCcw, Search, Star, ThumbsDown, TrendingUp } from 'lucide-react';
import Layout from '../../Layout';
import {
  CONTACT_CHANNEL, NPS_DETRACTOR, SURVEY_MAX_ATTEMPTS, customerById, isDoNotCall, rangeBounds, useCallCenter,
  type RangePreset, type Survey,
} from '../CustomerDemo/callCenterStore';
import { softphone } from '../CustomerDemo/softphone';
import Softphone from '../CustomerDemo/components/Softphone';
import { isDoneSurvey, isLowSurvey, pendingSurveys, surveyScore, surveyStats, type PendingSurvey } from './cskhStats';
import { CustomerCell, Kpi, PageHeader, Stars, detailUrl, fmtTime } from './shared';
import { useCskhDemoAutoload } from './demoData';

type SurveyTab = 'pending' | 'done' | 'low' | 'skipped';
const TABS: { key: SurveyTab; label: string }[] = [
  { key: 'pending', label: 'Chờ khảo sát' },
  { key: 'done', label: 'Đã khảo sát' },
  { key: 'low', label: 'Điểm thấp' },
  { key: 'skipped', label: 'Từ chối / Không liên lạc' },
];
const RANGES: { value: RangePreset; label: string }[] = [
  { value: '7d', label: '7 ngày qua' }, { value: '30d', label: '30 ngày qua' }, { value: 'all', label: 'Tất cả thời gian' },
];

/** Khảo sát hài lòng sau dịch vụ: danh sách chờ khảo sát, kết quả CSAT / NPS, điểm thấp */
export default function CskhSurveysPage() {
  useCskhDemoAutoload();
  const s = useCallCenter();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as SurveyTab) || 'pending';
  const range = (params.get('range') as RangePreset) || '30d';
  const q = (params.get('q') || '').trim().toLowerCase();
  const go = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  const now = Date.now();
  const bounds = rangeBounds(range, null, null, now);
  const matchName = (customerId: number) => {
    if (!q) return true;
    const c = customerById(s, customerId);
    return !!c && `${c.name} ${c.phones.join(' ')}`.toLowerCase().includes(q);
  };
  const inRange = s.surveys.filter(sv => !bounds || (sv.at >= bounds[0] && sv.at <= bounds[1]));
  const stats = surveyStats(inRange);
  const pending = pendingSurveys(s, now).filter(p => matchName(p.appointment.customerId));
  const dueCount = pending.filter(p => p.dueAt <= now).length;
  const surveys = inRange
    .filter(sv => matchName(sv.customerId))
    .filter(sv => (tab === 'done' ? isDoneSurvey(sv) : tab === 'low' ? isLowSurvey(sv) || (typeof sv.nps === 'number' && sv.nps <= NPS_DETRACTOR) : !isDoneSurvey(sv)))
    .sort((a, b) => b.at - a.at);
  const apptOf = (id?: string) => (id ? s.appointments.find(a => a.id === id) : undefined);
  const counts: Record<SurveyTab, number> = {
    pending: pending.length,
    done: stats.done,
    low: inRange.filter(sv => isLowSurvey(sv) || (isDoneSurvey(sv) && typeof sv.nps === 'number' && sv.nps <= NPS_DETRACTOR)).length,
    skipped: stats.skipped,
  };

  const pendingColumns: ColumnsType<PendingSurvey> = [
    { title: 'KHÁCH HÀNG', key: 'c', width: 220, render: (_, p) => <CustomerCell customer={customerById(s, p.appointment.customerId)} /> },
    {
      title: 'DỊCH VỤ', key: 'svc',
      render: (_, p) => (
        <div>
          <div className="text-[13px] font-medium text-gray-800 dark:text-gray-200">{p.appointment.service}</div>
          <div className="text-[11px] text-gray-400">{p.appointment.practitioner ? `${p.appointment.practitioner} · ` : ''}{p.appointment.branch}</div>
        </div>
      ),
    },
    { title: 'NGÀY LÀM', key: 'at', width: 120, render: (_, p) => <span className="text-[13px]">{dayjs(p.appointment.at).format('DD/MM/YYYY')}</span> },
    {
      title: 'HẠN KHẢO SÁT', key: 'due', width: 150,
      render: (_, p) => (
        <div>
          <div className={`text-[13px] ${p.overdue ? 'text-rose-600 font-semibold' : p.dueAt <= now ? 'text-amber-600 font-medium' : 'text-gray-500'}`}>
            {dayjs(p.dueAt).format('DD/MM/YYYY')}
          </div>
          <div className="text-[11px] text-gray-400">{p.overdue ? 'Quá hạn' : p.dueAt <= now ? 'Đến hạn hôm nay' : 'Chưa đến hạn'}</div>
        </div>
      ),
    },
    {
      title: 'LẦN THỬ', key: 'tries', width: 100,
      render: (_, p) => <span className={`text-xs ${p.attempts ? 'text-amber-600' : 'text-gray-400'}`}>{p.attempts}/{SURVEY_MAX_ATTEMPTS} không liên lạc</span>,
    },
    {
      title: '', key: 'act', width: 190, fixed: 'right',
      render: (_, p) => {
        const c = customerById(s, p.appointment.customerId);
        const phone = c?.phones[0] || '';
        return (
          <div className="flex items-center gap-1.5 justify-end">
            <Tooltip title={isDoNotCall(s, phone) ? 'Khách đang chặn gọi' : undefined}>
              <Button size="small" icon={<Phone size={12} />} disabled={!phone || isDoNotCall(s, phone)} onClick={() => softphone.dial(phone)}>Gọi</Button>
            </Tooltip>
            <Button size="small" type="primary" icon={<Star size={12} />} onClick={() => navigate(detailUrl(c, phone, `&survey=${p.appointment.id}`))}>Khảo sát</Button>
          </div>
        );
      },
    },
  ];

  const surveyColumns: ColumnsType<Survey> = [
    { title: 'KHÁCH HÀNG', key: 'c', width: 220, render: (_, sv) => <CustomerCell customer={customerById(s, sv.customerId)} /> },
    {
      title: 'DỊCH VỤ', key: 'svc', width: 180,
      render: (_, sv) => { const a = apptOf(sv.appointmentId); return a ? <div><div className="text-[13px]">{a.service}</div><div className="text-[11px] text-gray-400">{a.practitioner || a.branch}</div></div> : <span className="text-gray-300">—</span>; },
    },
    ...(tab === 'skipped' ? [{
      title: 'KẾT QUẢ', key: 'st', width: 160,
      render: (_: unknown, sv: Survey) => <span className="text-xs text-gray-600">{sv.status === 'declined' ? 'Khách từ chối' : 'Không liên lạc được'}</span>,
    }] : [
      {
        title: 'ĐIỂM', key: 'score', width: 210,
        render: (_: unknown, sv: Survey) => (
          <div className="text-xs space-y-0.5">
            <div className="font-semibold text-sm">{surveyScore(sv).toFixed(1)}★</div>
            <div className="text-gray-500">DV <Stars v={sv.service} /> · TĐ <Stars v={sv.attitude} />{typeof sv.practitionerScore === 'number' && <> · BS/KTV <Stars v={sv.practitionerScore} /></>}</div>
          </div>
        ),
      },
      {
        title: 'NPS', key: 'nps', width: 70,
        render: (_: unknown, sv: Survey) => typeof sv.nps === 'number'
          ? <span className={`font-semibold ${sv.nps >= 9 ? 'text-emerald-600' : sv.nps <= NPS_DETRACTOR ? 'text-rose-600' : 'text-amber-600'}`}>{sv.nps}</span>
          : <span className="text-gray-300">—</span>,
      },
    ]),
    { title: 'NHẬN XÉT', key: 'cmt', render: (_, sv) => <div className="text-[13px] text-gray-700 dark:text-gray-300 max-w-[320px] line-clamp-3">{sv.comment || <span className="text-gray-300">—</span>}</div> },
    {
      title: 'NGƯỜI KHẢO SÁT', key: 'by', width: 170,
      render: (_, sv) => <div className="text-xs"><div>{sv.by}{sv.channel ? ` · ${CONTACT_CHANNEL[sv.channel]}` : ''}</div><div className="text-gray-400">{fmtTime(sv.at, now)}</div></div>,
    },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Khảo sát hài lòng" desc="Khảo sát khách sau dịch vụ — điểm thấp tự tạo khiếu nại để xử lý"
          extra={<Select className="w-[170px]" value={range} onChange={v => go({ range: v === '30d' ? null : v })} options={RANGES} />} />

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <Kpi icon={<Hourglass size={18} />} tone="bg-amber-50 text-amber-600" label="Chờ khảo sát" value={pending.length}
            hint={`${dueCount} đã đến hạn`} onClick={() => go({ tab: null })} active={tab === 'pending'} />
          <Kpi icon={<ClipboardList size={18} />} tone="bg-blue-50 text-blue-600" label="Đã khảo sát" value={stats.done}
            hint={`${stats.skipped} từ chối / không liên lạc`} onClick={() => go({ tab: 'done' })} active={tab === 'done'} />
          <Kpi icon={<Star size={18} />} tone="bg-emerald-50 text-emerald-600" label="CSAT trung bình" value={stats.csat === null ? '—' : `${stats.csat.toFixed(1)}★`}
            hint={stats.done ? `${stats.low} khảo sát điểm thấp` : 'Chưa có dữ liệu'} onClick={() => go({ tab: 'low' })} active={tab === 'low'} />
          <Kpi icon={<TrendingUp size={18} />} tone="bg-violet-50 text-violet-600" label="NPS" value={stats.nps === null ? '—' : stats.nps}
            hint="% giới thiệu − % không giới thiệu" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Input allowClear className="!w-full sm:!w-64" prefix={<Search size={14} className="text-gray-400" />} placeholder="Tìm tên, số điện thoại"
            value={params.get('q') || ''} onChange={e => go({ q: e.target.value || null })} />
          {params.get('q') && (
            <button type="button" onClick={() => go({ q: null })} className="text-xs text-gray-500 hover:text-[#3e91ab] flex items-center gap-1 cursor-pointer">
              <RotateCcw size={12} /> Xoá tìm kiếm
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {TABS.map(t => (
            <button key={t.key} type="button" onClick={() => go({ tab: t.key === 'pending' ? null : t.key })}
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium border shrink-0 flex items-center gap-1.5 cursor-pointer transition ${tab === t.key ? 'bg-gray-800 text-white border-gray-800' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:bg-gray-50'}`}>
              {t.key === 'low' && <ThumbsDown size={13} />} {t.label}
              <span className={`text-[11px] px-1.5 rounded-full ${tab === t.key ? 'bg-white/25' : 'bg-black/5'}`}>{counts[t.key]}</span>
            </button>
          ))}
        </div>

        {tab === 'pending' ? (
          <Table columns={pendingColumns} dataSource={pending} rowKey={p => p.appointment.id} size="middle" scroll={{ x: 'max-content' }}
            rowClassName={p => (p.overdue ? 'bg-rose-50/40 dark:bg-rose-950/10' : '')}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không còn lần làm dịch vụ nào chờ khảo sát" /> }}
            pagination={{ defaultPageSize: 10, showSizeChanger: true, showTotal: (t, r) => `Hiển thị ${r[0]}–${r[1]} / ${t}` }} />
        ) : (
          <Table columns={surveyColumns} dataSource={surveys} rowKey="id" size="middle" scroll={{ x: 'max-content' }}
            rowClassName={sv => (isLowSurvey(sv) ? 'bg-rose-50/40 dark:bg-rose-950/10' : '')}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có khảo sát nào trong khoảng thời gian này" /> }}
            pagination={{ defaultPageSize: 10, showSizeChanger: true, showTotal: (t, r) => `Hiển thị ${r[0]}–${r[1]} / ${t} khảo sát` }} />
        )}
      </div>
      <Softphone />
    </Layout>
  );
}
